import { useEffect, useState } from "react";
import { Star, Tag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  type DocumentPublishTypeTag,
  type PublishType,
  listDocumentPublishTypeTags,
  setDocumentPublishTypeStyleSample,
  setDocumentPublishTypes,
} from "@/lib/workspacePublishTypes";

type Props = {
  documentId: string;
  workspaceId: string;
  publishTypes: PublishType[];
};

export const PublishTypeChipSelector = ({ documentId, workspaceId, publishTypes }: Props) => {
  const [tags, setTags] = useState<DocumentPublishTypeTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    listDocumentPublishTypeTags(documentId)
      .then((rows) => {
        if (active) setTags(rows);
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [documentId]);

  const tagMap = new Map(tags.map((t) => [t.publishTypeId, t]));
  const tagged = tags.length;
  const samples = tags.filter((t) => t.isStyleSample).length;

  const toggleTag = async (publishTypeId: string) => {
    const isOn = tagMap.has(publishTypeId);
    const nextIds = isOn
      ? tags.filter((t) => t.publishTypeId !== publishTypeId).map((t) => t.publishTypeId)
      : [...tags.map((t) => t.publishTypeId), publishTypeId];
    const prev = tags;
    // Optimistic: assume new tags start unstarred; removing also strips star
    setTags(
      isOn
        ? tags.filter((t) => t.publishTypeId !== publishTypeId)
        : [...tags, { publishTypeId, isStyleSample: false }],
    );
    setBusy(true);
    try {
      await setDocumentPublishTypes({
        documentId,
        workspaceId,
        publishTypeIds: nextIds,
      });
    } catch (err) {
      setTags(prev);
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const toggleStyleSample = async (publishTypeId: string) => {
    const existing = tagMap.get(publishTypeId);
    if (!existing) return;
    const next = !existing.isStyleSample;
    const prev = tags;
    setTags(tags.map((t) => (t.publishTypeId === publishTypeId ? { ...t, isStyleSample: next } : t)));
    setBusy(true);
    try {
      await setDocumentPublishTypeStyleSample({
        documentId,
        publishTypeId,
        isStyleSample: next,
      });
    } catch (err) {
      setTags(prev);
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const ptById = new Map(publishTypes.map((p) => [p.id, p]));
  const triggerLabel = tagged === 0 ? "Tag publish type" : "Edit";

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-wrap items-center justify-end gap-1.5">
        {tags.map((tag) => {
          const pt = ptById.get(tag.publishTypeId);
          if (!pt) return null;
          return (
            <Badge
              key={tag.publishTypeId}
              variant={tag.isStyleSample ? "default" : "outline"}
              className="h-6 gap-1 px-2 text-[10px]"
            >
              {tag.isStyleSample && <Star className="h-2.5 w-2.5 fill-current" />}
              {pt.label}
              {tag.isStyleSample && " sample"}
            </Badge>
          );
        })}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs"
            disabled={loading}
          >
            <Tag className="h-3.5 w-3.5" />
            {triggerLabel}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-2">
          <div className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Publish type
          </div>
          <p className="mb-2 px-1 text-[11px] leading-snug text-muted-foreground">
            Tag this doc with the type(s) of content it represents. Star a type to designate this
            doc as a <span className="font-medium text-foreground">Writing Style Sample</span> the
            AI imitates when generating that format.
          </p>
          {publishTypes.length === 0 ? (
            <div className="p-2 text-xs text-muted-foreground">
              No publish types defined yet. Add some from the Corpus header.
            </div>
          ) : (
            <ul className="max-h-72 space-y-0.5 overflow-y-auto">
              {publishTypes.map((pt) => {
                const tag = tagMap.get(pt.id);
                const isOn = !!tag;
                const starred = !!tag?.isStyleSample;
                return (
                  <li
                    key={pt.id}
                    className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted/60"
                  >
                    <Checkbox
                      id={`pt-${pt.id}`}
                      checked={isOn}
                      disabled={busy}
                      onCheckedChange={() => void toggleTag(pt.id)}
                    />
                    <label
                      htmlFor={`pt-${pt.id}`}
                      className="flex-1 cursor-pointer text-sm"
                    >
                      {pt.label}
                    </label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          disabled={busy || !isOn}
                          onClick={() => void toggleStyleSample(pt.id)}
                          className={cn(
                            "rounded p-1 transition-colors",
                            !isOn && "opacity-30",
                            isOn && !starred && "text-muted-foreground hover:text-foreground",
                            starred && "text-amber-500 hover:text-amber-600",
                          )}
                          aria-label={
                            starred
                              ? `Unstar — remove as Writing Style Sample for ${pt.label}`
                              : `Star — use as Writing Style Sample for ${pt.label}`
                          }
                        >
                          <Star
                            className={cn("h-3.5 w-3.5", starred && "fill-current")}
                          />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        {isOn
                          ? starred
                            ? `Writing Style Sample for ${pt.label}`
                            : `Star to use as Writing Style Sample for ${pt.label}`
                          : "Tag this type first"}
                      </TooltipContent>
                    </Tooltip>
                  </li>
                );
              })}
            </ul>
          )}
        </PopoverContent>
      </Popover>
      </div>
    </TooltipProvider>
  );
};

